import {Box} from '@mantine/core';
import React, {useState} from 'react';

import PlateNavigator from 'notebook/integrations/plate-maps/scratch-plates/PlateNavigator';
import {SCRATCH_PLATE_VIEWER_MIN_HEIGHT} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewConstants';
import {ContentsLayerScratchPlateViewer} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewer';
import useFilledScratchPlateData from 'notebook/integrations/plate-maps/scratch-plates/useFilledScratchPlateData';
import Pane from 'ui-library/pane/Pane';

export interface ScratchPlatePreviewItem {
  id: string;
  name: string | null;
}

interface ScratchPlateOutputPreviewProps {
  scratchPlates: Array<ScratchPlatePreviewItem>;
  title: string;
}

function ScratchPlatePreviewContent({scratchPlateId}: {scratchPlateId: string}): JSX.Element {
  const {result} = useFilledScratchPlateData(scratchPlateId);
  return <ContentsLayerScratchPlateViewer result={result} />;
}

/** Render one document plate group as a pane, paging through multiple plates one at a time. */
export default function ScratchPlateOutputPreview({
  scratchPlates,
  title,
}: ScratchPlateOutputPreviewProps): JSX.Element {
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Reset paging when the plate set changes so the navigator can't linger on a stale plate.
  const plateIdsKey = scratchPlates.map((plate) => plate.id).join(',');
  const [prevPlateIdsKey, setPrevPlateIdsKey] = useState(plateIdsKey);
  if (plateIdsKey !== prevPlateIdsKey) {
    setPrevPlateIdsKey(plateIdsKey);
    setSelectedIndex(0);
  }
  const safeIndex = Math.min(selectedIndex, Math.max(scratchPlates.length - 1, 0));
  const selectedPlate = scratchPlates[safeIndex];

  return (
    <Pane title={title}>
      <Box p="md">
        {scratchPlates.length > 1 && (
          <PlateNavigator
            totalPlates={scratchPlates.length}
            currentIndex={safeIndex}
            onChangeIndex={setSelectedIndex}
          />
        )}
        {selectedPlate != null && (
          <Box miw={0} mih={SCRATCH_PLATE_VIEWER_MIN_HEIGHT}>
            <ScratchPlatePreviewContent scratchPlateId={selectedPlate.id} />
          </Box>
        )}
      </Box>
    </Pane>
  );
}
